import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { FaCreditCard, FaSearch, FaCheckCircle, FaExclamationTriangle, FaShieldAlt, FaCalendarAlt, FaInfoCircle } from 'react-icons/fa';

const CreditCardPaymentModal = ({ show, onHide, onSuccess }) => {
  const [selectedBank, setSelectedBank] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [cardDetails, setCardDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingCard, setFetchingCard] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Credit card providers/banks
  const banks = [
    { id: 'hdfc', name: 'HDFC Bank', logo: '/banks/hdfc.png', color: 'primary' },
    { id: 'icici', name: 'ICICI Bank', logo: '/banks/icici.png', color: 'danger' },
    { id: 'sbi', name: 'State Bank of India', logo: '/banks/sbi.png', color: 'success' },
    { id: 'axis', name: 'Axis Bank', logo: '/banks/axis.png', color: 'warning' },
    { id: 'kotak', name: 'Kotak Mahindra Bank', logo: '/banks/kotak.png', color: 'info' },
    { id: 'indusind', name: 'IndusInd Bank', logo: '/banks/indusind.png', color: 'secondary' },
    { id: 'yes', name: 'YES Bank', logo: '/banks/yes.png', color: 'primary' },
    { id: 'citi', name: 'Citibank', logo: '/banks/citi.png', color: 'dark' },
    { id: 'hsbc', name: 'HSBC Bank', logo: '/banks/hsbc.png', color: 'danger' },
    { id: 'american_express', name: 'American Express', logo: '/banks/amex.png', color: 'success' }
  ];

  const cardTypes = ['Visa', 'Mastercard', 'RuPay', 'American Express'];

  const formatCardNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');
    // Add spaces every 4 digits
    const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();
    return formatted.slice(0, 19); // Max 16 digits + 3 spaces
  };

  const validateCardNumber = (number) => {
    const cleaned = number.replace(/\s/g, '');
    return /^[0-9]{12,19}$/.test(cleaned);
  };

  const detectCardType = (number) => {
    const cleaned = number.replace(/\s/g, '');
    if (cleaned.startsWith('4')) return 'Visa';
    if (cleaned.startsWith('5') || cleaned.startsWith('2')) return 'Mastercard';
    if (cleaned.startsWith('6')) return 'RuPay';
    if (cleaned.startsWith('3')) return 'American Express';
    return 'Unknown';
  };

  const fetchCardDetails = async () => {
    if (!selectedBank) {
      setMessage({ type: 'error', text: 'Please select a bank' });
      return;
    }

    if (!validateCardNumber(cardNumber)) {
      setMessage({ type: 'error', text: 'Please enter a valid card number (12-19 digits)' });
      return;
    }

    setFetchingCard(true);
    setMessage({ type: '', text: '' });
    setCardDetails(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2500));

      const cardType = detectCardType(cardNumber);
      const bank = banks.find(b => b.id === selectedBank);

      // Mock card details
      const mockCardDetails = {
        cardholderName: 'CARDHOLDER NAME',
        cardNumber: cardNumber,
        cardType: cardType,
        bank: bank?.name,
        outstandingAmount: Math.floor(Math.random() * (50000 - 1000) + 1000),
        minimumAmount: Math.floor(Math.random() * (2000 - 500) + 500),
        dueDate: new Date(Date.now() + Math.floor(Math.random() * 20 + 5) * 24 * 60 * 60 * 1000).toLocaleDateString(),
        billDate: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        creditLimit: 100000,
        availableLimit: Math.floor(Math.random() * 80000 + 20000),
        lastPayment: Math.floor(Math.random() * 5000 + 1000),
        lastPaymentDate: new Date(Date.now() - Math.floor(Math.random() * 30 + 10) * 24 * 60 * 60 * 1000).toLocaleDateString(),
        status: Math.random() > 0.3 ? 'Active' : 'Overdue'
      };

      setCardDetails(mockCardDetails);
      setAmount(mockCardDetails.minimumAmount.toString());

      setMessage({ 
        type: 'success', 
        text: `Card details fetched successfully. Outstanding: ₹${mockCardDetails.outstandingAmount}` 
      });

    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch card details. Please try again.' });
    } finally {
      setFetchingCard(false);
    }
  };

  const handlePayment = async () => {
    if (!cardDetails) {
      setMessage({ type: 'error', text: 'Please fetch card details first' });
      return;
    }

    if (!amount || parseFloat(amount) < cardDetails.minimumAmount) {
      setMessage({ 
        type: 'error', 
        text: `Minimum payment amount is ₹${cardDetails.minimumAmount}` 
      });
      return;
    }

    if (parseFloat(amount) > cardDetails.outstandingAmount) {
      setMessage({ 
        type: 'error', 
        text: `Payment amount cannot exceed outstanding amount of ₹${cardDetails.outstandingAmount}` 
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 4000));

      const paymentData = {
        transactionId: 'CC' + Date.now(),
        cardNumber: cardNumber.slice(-4).padStart(cardNumber.length, '*'),
        amount: parseFloat(amount),
        bank: cardDetails.bank,
        cardType: cardDetails.cardType,
        status: 'Success',
        timestamp: new Date().toLocaleString()
      };

      setMessage({ 
        type: 'success', 
        text: `Credit card payment successful! Transaction ID: ${paymentData.transactionId}` 
      });

      if (onSuccess) {
        onSuccess(paymentData);
      }

      // Auto-close after 3 seconds
      setTimeout(() => {
        onHide();
      }, 3000);

    } catch (error) {
      setMessage({ type: 'error', text: 'Payment failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedBank('');
    setCardNumber('');
    setAmount('');
    setCardDetails(null);
    setMessage({ type: '', text: '' });
  };

  const quickAmounts = cardDetails ? [
    cardDetails.minimumAmount,
    Math.floor(cardDetails.outstandingAmount * 0.25),
    Math.floor(cardDetails.outstandingAmount * 0.5),
    Math.floor(cardDetails.outstandingAmount * 0.75),
    cardDetails.outstandingAmount
  ] : [];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaCreditCard className="me-2" />
          Credit Card Bill Payment
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Bank Selection */}
        {!selectedBank && (
          <div className="mb-4">
            <h6 className="mb-3">Select Your Bank</h6>
            <Row>
              {banks.map((bank) => (
                <Col md={3} sm={4} xs={6} key={bank.id} className="mb-3">
                  <Card 
                    className="h-100 bank-card cursor-pointer text-center"
                    onClick={() => setSelectedBank(bank.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body className="p-3">
                      <FaCreditCard className={`text-${bank.color} mb-2`} size={24} />
                      <div className="small fw-bold">{bank.name}</div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Card Details Form */}
        {selectedBank && (
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <strong>
                  <FaCreditCard className="me-2" />
                  {banks.find(b => b.id === selectedBank)?.name}
                </strong>
                <Button variant="link" size="sm" onClick={handleReset}>
                  Change Bank
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Credit Card Number *</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Enter 16-digit credit card number"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                        maxLength={19}
                      />
                      <Button 
                        variant="outline-primary" 
                        onClick={fetchCardDetails}
                        disabled={!cardNumber || fetchingCard}
                      >
                        {fetchingCard ? <Spinner size="sm" /> : <FaSearch />}
                      </Button>
                    </InputGroup>
                    {cardNumber && (
                      <Form.Text className="text-muted">
                        Detected: {detectCardType(cardNumber)}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Amount (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Enter amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      min={cardDetails?.minimumAmount || 1}
                      max={cardDetails?.outstandingAmount || 100000}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Card Details */}
        {cardDetails && (
          <Card className="mb-4">
            <Card.Header className="bg-success text-white">
              <FaCheckCircle className="me-2" />
              Card Details
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-2"><strong>Cardholder:</strong> {cardDetails.cardholderName}</div>
                  <div className="mb-2"><strong>Card Type:</strong> {cardDetails.cardType}</div>
                  <div className="mb-2"><strong>Card Number:</strong> **** **** **** {cardNumber.slice(-4)}</div>
                  <div className="mb-2"><strong>Outstanding Amount:</strong> ₹{cardDetails.outstandingAmount.toLocaleString()}</div>
                </Col>
                <Col md={6}>
                  <div className="mb-2"><strong>Minimum Amount Due:</strong> ₹{cardDetails.minimumAmount.toLocaleString()}</div>
                  <div className="mb-2"><strong>Due Date:</strong> {cardDetails.dueDate}</div>
                  <div className="mb-2"><strong>Credit Limit:</strong> ₹{cardDetails.creditLimit.toLocaleString()}</div>
                  <div className="mb-2">
                    <strong>Status:</strong> 
                    <Badge bg={cardDetails.status === 'Active' ? 'success' : 'danger'} className="ms-2">
                      {cardDetails.status}
                    </Badge>
                  </div>
                </Col>
              </Row>
              
              {cardDetails.status === 'Overdue' && (
                <Alert variant="danger" className="mt-3 mb-0">
                  <FaExclamationTriangle className="me-2" />
                  Your credit card payment is overdue. Please pay immediately to avoid late fees and impact on credit score.
                </Alert>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Quick Payment Options */}
        {cardDetails && (
          <div className="mb-4">
            <h6 className="mb-3">Quick Payment Options</h6>
            <div className="d-flex flex-wrap gap-2">
              {quickAmounts.map((quickAmount, index) => {
                const labels = ['Minimum', '25%', '50%', '75%', 'Full'];
                return (
                  <Button
                    key={index}
                    variant={amount === quickAmount.toString() ? "primary" : "outline-primary"}
                    size="sm"
                    onClick={() => setAmount(quickAmount.toString())}
                  >
                    {labels[index]} (₹{quickAmount.toLocaleString()})
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {/* Security Notice */}
        <Alert variant="info" className="mb-3">
          <FaShieldAlt className="me-2" />
          <strong>Secure Payment:</strong> Your transaction is protected by 256-bit SSL encryption. 
          We never store your complete card details.
        </Alert>

        {/* Message */}
        {message.text && (
          <Alert variant={message.type === 'error' ? 'danger' : 'success'} className="mb-3">
            {message.text}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {selectedBank && (
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Cancel
            </Button>
            <Button 
              variant="success" 
              onClick={handlePayment}
              disabled={loading || !cardDetails || !amount}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <FaCreditCard className="me-2" />
                  Pay ₹{amount || '0'}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreditCardPaymentModal;
